/**
 * /transport/international/apply — Self-service application form for
 * the international courier persona.
 *
 * Submits to `POST /api/transport/international/apply` which:
 *   1) creates a `pending_payment` application row
 *   2) returns a Stripe Checkout URL for the $9 verification fee
 *      (includes the first lead free)
 *   3) on payment, the webhook flips status → `pending_review`
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { isNativeApp } from "@/lib/nativeBridge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AirplaneTiltIcon as AirplaneTilt, ShieldCheckIcon as ShieldCheck, ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react";

export default function InternationalTransportApply() {
  const navigate = useNavigate();
  const [legal, setLegal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    display_name: "",
    legal_name: "",
    email: "",
    phone: "",
    home_country: "",
    corridors_text: "",
    years_in_business: 0,
    trips_completed_estimate: 0,
    ipata_member_number: "",
    cdc_dog_import_permit_number: "",
    usda_aphis_importer_number: "",
    iata_lar_certified: false,
    insurance_carrier: "",
    insurance_liability_usd: 0,
    insurance_certificate_url: "",
    airlines_text: "",
    customs_broker_contact: "",
    website_url: "",
    bio: "",
    photo_url: "",
    accepted_agreement: false,
  });

  useEffect(() => {
    api
      .get("/transport/international/legal")
      .then((r) => setLegal(r.data))
      .catch(() => {});
  }, []);

  const set = (k) => (e) =>
    setForm((s) => ({
      ...s,
      [k]: e?.target ? e.target.value : e,
    }));

  const submit = async () => {
    if (!form.accepted_agreement) {
      toast.error("Please accept the Lead Pass Agreement to continue.");
      return;
    }
    if (!form.display_name || !form.email || !form.home_country) {
      toast.error("Display name, email, and home country are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        corridors: form.corridors_text
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        airlines_approved: form.airlines_text
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        years_in_business: parseInt(form.years_in_business, 10) || 0,
        trips_completed_estimate:
          parseInt(form.trips_completed_estimate, 10) || 0,
        insurance_liability_usd:
          parseInt(form.insurance_liability_usd, 10) || 0,
      };
      delete payload.corridors_text;
      delete payload.airlines_text;
      const r = await api.post("/transport/international/apply", payload);
      if (r.data?.stripe_checkout_url) {
        window.location.href = r.data.stripe_checkout_url;
        return;
      }
      toast.success(
        "Application received. We'll be in touch about the $9 verification fee."
      );
      navigate("/dashboard");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Application failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div
        className="max-w-3xl mx-auto px-4 py-8"
        data-testid="intl-transport-apply"
      >
        <button
          onClick={() => navigate("/transport/international")}
          className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> Back to directory
        </button>

        <div className="text-[11px] tracking-[3px] text-[#8A6A00] font-bold uppercase mb-3">
          International Courier — Lead Pass Application
        </div>
        <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
          <AirplaneTilt size={36} weight="duotone" /> Become a verified courier
        </h1>
        <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed max-w-2xl">
          We send you <strong>verified buyer leads</strong> for international
          dog transport — and <strong>your first lead is free</strong>. All
          money for the actual transport, customs, and carriage flows between
          you and the buyer directly — Armstrong never holds it. Application
          is reviewed within 5 business days.
        </p>

        {/* ============= Pricing block (web only — App Review 3.1.1) ============= */}
        {legal?.pricing && !isNativeApp() && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PriceTile label="Verify" value={`$${legal.pricing.application_fee_usd}`} sub="one-time · 1st lead free" />
            <PriceTile label="Per lead" value={`$${legal.pricing.pay_per_lead_usd}`} sub="pay as you go" />
            <PriceTile label="Pro" value={`$${legal.pricing.monthly_usd}/mo`} sub="unlimited + featured" />
            <PriceTile
              label="Or annually"
              value={`$${(legal.pricing.yearly_usd || 0).toLocaleString()}/yr`}
              sub="save ~16%"
            />
          </div>
        )}

        {/* ============= Form ============= */}
        <div className="mt-8 bg-white border border-[#E5D08A] rounded-xl p-6 space-y-6">
          <Group title="About you">
            <Input
              placeholder="Display name (shown publicly)"
              value={form.display_name}
              onChange={set("display_name")}
              data-testid="intl-apply-display-name"
            />
            <Input
              placeholder="Legal name (for agreement)"
              value={form.legal_name}
              onChange={set("legal_name")}
            />
            <Input
              placeholder="Email"
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="intl-apply-email"
            />
            <Input
              placeholder="Phone (with country code)"
              value={form.phone}
              onChange={set("phone")}
            />
            <Input
              placeholder="Home country"
              value={form.home_country}
              onChange={set("home_country")}
              data-testid="intl-apply-country"
            />
            <Input
              placeholder="Website (optional)"
              value={form.website_url}
              onChange={set("website_url")}
            />
          </Group>

          <Group title="Service corridors">
            <Textarea
              placeholder={
                "One corridor per line, e.g.\nRomania → JFK\nSouth Korea → LAX"
              }
              rows={4}
              value={form.corridors_text}
              onChange={set("corridors_text")}
              data-testid="intl-apply-corridors"
            />
          </Group>

          <Group title="Experience">
            <Input
              type="number"
              placeholder="Years in business"
              value={form.years_in_business}
              onChange={set("years_in_business")}
              min={0}
            />
            <Input
              type="number"
              placeholder="Trips completed (estimate)"
              value={form.trips_completed_estimate}
              onChange={set("trips_completed_estimate")}
              min={0}
            />
          </Group>

          <Group title="Compliance & credentials">
            <Input
              placeholder="IPATA member number"
              value={form.ipata_member_number}
              onChange={set("ipata_member_number")}
            />
            <Input
              placeholder="CDC Dog Import Permit #"
              value={form.cdc_dog_import_permit_number}
              onChange={set("cdc_dog_import_permit_number")}
            />
            <Input
              placeholder="USDA APHIS importer #"
              value={form.usda_aphis_importer_number}
              onChange={set("usda_aphis_importer_number")}
            />
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox
                checked={form.iata_lar_certified}
                onCheckedChange={(v) =>
                  setForm((s) => ({ ...s, iata_lar_certified: !!v }))
                }
              />
              <span>I am IATA Live Animal Regulations (LAR) certified</span>
            </label>
            <Input
              placeholder="Airlines approved to fly with (comma-separated)"
              value={form.airlines_text}
              onChange={set("airlines_text")}
            />
            <Input
              placeholder="Customs broker (name + email)"
              value={form.customs_broker_contact}
              onChange={set("customs_broker_contact")}
            />
          </Group>

          <Group title="Insurance">
            <Input
              placeholder="Carrier (e.g. Lloyd's of London)"
              value={form.insurance_carrier}
              onChange={set("insurance_carrier")}
            />
            <Input
              type="number"
              placeholder="Liability coverage (USD)"
              value={form.insurance_liability_usd}
              onChange={set("insurance_liability_usd")}
              min={0}
            />
            <Input
              placeholder="Certificate URL (Drive / Dropbox link)"
              value={form.insurance_certificate_url}
              onChange={set("insurance_certificate_url")}
            />
          </Group>

          <Group title="Your story">
            <Textarea
              placeholder="Short bio — clients want to know your story, what makes you different, and why they should trust you with their dog."
              rows={4}
              value={form.bio}
              onChange={set("bio")}
            />
            <Input
              placeholder="Profile photo URL"
              value={form.photo_url}
              onChange={set("photo_url")}
            />
          </Group>

          {/* ============= Agreement click-wrap ============= */}
          {legal?.courier_agreement && (
            <div className="bg-[#FBF7EC] border border-[#E5D08A] rounded-lg p-4">
              <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">
                Lead Pass Agreement · v{legal.version}
              </div>
              <div className="text-[12px] leading-relaxed text-[#3a3a3a] max-h-44 overflow-y-auto">
                {legal.courier_agreement}
              </div>
              <label className="flex items-start gap-2 mt-3 text-[12px]">
                <Checkbox
                  checked={form.accepted_agreement}
                  onCheckedChange={(v) =>
                    setForm((s) => ({ ...s, accepted_agreement: !!v }))
                  }
                  data-testid="intl-apply-ack"
                />
                <span>
                  I agree to the International Transporter Lead Pass
                  Agreement and confirm I am the importer/exporter of
                  record for every animal I move.
                </span>
              </label>
            </div>
          )}

          <Button
            onClick={submit}
            disabled={busy || !form.accepted_agreement}
            className="rounded-full bg-crimson hover:bg-ink text-white w-full h-11"
            data-testid="intl-apply-submit"
          >
            <ShieldCheck size={16} weight="duotone" className="mr-2" />
            {busy ? "Submitting…" : isNativeApp() ? "Submit — $9 verification fee (checkout in browser)" : "Submit & pay $9 verification fee"}
          </Button>
          <p className="text-[11px] text-[#8B8B8B] text-center">
            The $9 verification fee is non-refundable and covers credentials
            review. It includes 1 free lead credit — your first lead is free.
            After that, pay $29 per lead or go unlimited with Pro.
          </p>
        </div>
      </div>
    </AppShell>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function PriceTile({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5D08A] rounded-lg p-3 text-center">
      <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase">
        {label}
      </div>
      <div className="font-spectral text-2xl mt-1">{value}</div>
      <div className="text-[10px] text-[#8B8B8B]">{sub}</div>
    </div>
  );
}
